<template>
  <v-app>
    <Callback v-if="onCallbackPath" />
    <Unavailable v-else-if="session.status === 'unavailable'" />
    <NoAccess v-else-if="session.status === 'no-access'" />
    <OrgPicker v-else-if="session.status === 'org-pick'" />
    <Login v-else-if="session.status === 'signed-out' || session.status === 'error'" />
    <template v-else-if="session.status === 'ready'">
      <TopBar />
      <Sidebar v-model:rail="rail" />
      <!-- Only rendered at "/" — hosted modules mount straight onto
           document.body (outside this tree) for every other path, so this
           never competes with a module for the same screen real estate. -->
      <v-main v-if="showAppHome">
        <AppHome />
      </v-main>
    </template>
    <!-- single-spa mounts the tenant's modules straight onto document.body
         (single-spa-vue's default target — no host container needed);
         modules pad themselves below via --metaship-header-height. -->
    <div v-else class="standalone-loading">
      <OrbitalLoader />
    </div>
  </v-app>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, ref, watch } from "vue";
import { useSessionStore } from "@/stores/session";
import Sidebar from "./Sidebar.vue";
import OrbitalLoader from "./OrbitalLoader.vue";
import TopBar from "./TopBar.vue";
import AppHome from "./views/AppHome.vue";
import Login from "./views/Login.vue";
import Callback from "./views/Callback.vue";
import NoAccess from "./views/NoAccess.vue";
import Unavailable from "./views/Unavailable.vue";
import OrgPicker from "./views/OrgPicker.vue";

const RAIL_STORAGE_KEY = "metaship:standalone:railCollapsed";

export default defineComponent({
  name: "App",
  components: { Sidebar, TopBar, AppHome, OrbitalLoader, Login, Callback, NoAccess, Unavailable, OrgPicker },
  setup() {
    const session = useSessionStore();
    const path = ref(window.location.pathname);
    // Callback rendering has two triggers, OR'd — both are load-bearing:
    // 1. `path === "/auth0/callback"` ALONE (any status): the return from
    //    Auth0 is a full-page load, so the pre-redirect "authenticating"
    //    status died with the old page — the fresh boot arrives here as
    //    "signed-out". Requiring an auth status here meant nobody ever
    //    exchanged the code (boer.metaship.ai first login, 2026-08-27: URL
    //    flipped to /auth0/callback?code=… and sat on the Login screen).
    // 2. `status === "authenticating"`: Callback.vue clears the URL before
    //    its async work, so `path` leaves "/auth0/callback" almost
    //    immediately; this keeps the spinner mounted through the code
    //    exchange. `completeAuth0Callback` sets it synchronously on its
    //    first line, so there is no gap between the two conditions.
    // Every terminal status the flow resolves to (ready/org-pick/no-access/
    // error) ends "authenticating" while the path is already "/", so the
    // spinner always exits — the stuck-spinner case this gate originally
    // guarded against stays covered.
    const onCallbackPath = computed(
      () => path.value === "/auth0/callback" || session.status === "authenticating",
    );

    const rail = ref(readRailPreference());
    watch(rail, (value) => {
      try {
        localStorage.setItem(RAIL_STORAGE_KEY, value ? "1" : "0");
      } catch {
        // best-effort
      }
      publishNavWidth(value);
    });

    const showAppHome = computed(() => path.value === "/" && !!session.tenant?.homePage?.enabled);

    function onRoutingEvent(): void {
      path.value = window.location.pathname;
    }

    // Re-read the URL on every status change. Callback.vue clears the callback
    // URL with `history.replaceState`, which fires NEITHER `popstate` NOR
    // `single-spa:before-routing-event` — single-spa only emits routing events
    // once `singleSpa.start()` has run, and start() waits for status "ready"
    // (main.ts). So during sign-in `path` stayed at "/auth0/callback" and
    // `onCallbackPath` never went false: a visitor whose account resolves to
    // the ORG PICKER sat on "Completing sign-in…" forever, and only a manual
    // refresh showed the picker (reported 2026-08-27). The single-org path hid
    // the bug — it ends in a navigateToUrl(home) that does fire the event.
    watch(() => session.status, onRoutingEvent);

    onMounted(() => {
      publishNavWidth(rail.value);
      document.documentElement.style.setProperty("--metaship-footer-height", "0px");
      // Matches TopBar's `density="compact"` app-bar height exactly (Vuetify's
      // compact app-bar is 48px) — the single source of truth every hosted
      // module's `padding-top: var(--metaship-header-height)` reads.
      document.documentElement.style.setProperty("--metaship-header-height", "48px");
      window.addEventListener("popstate", onRoutingEvent);
      window.addEventListener("single-spa:before-routing-event", onRoutingEvent);
    });
    onUnmounted(() => {
      window.removeEventListener("popstate", onRoutingEvent);
      window.removeEventListener("single-spa:before-routing-event", onRoutingEvent);
    });

    return { session, onCallbackPath, rail, showAppHome };
  },
});

function readRailPreference(): boolean {
  try {
    return localStorage.getItem(RAIL_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function publishNavWidth(rail: boolean): void {
  document.documentElement.style.setProperty("--metaship-nav-width", rail ? "56px" : "230px");
}
</script>
